import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import { BENCHMARK_SINGLEAPPROVALQUOTE, DATE_FORMAT_UPDATED } from "../../constants/app.constants";
import {
  updateSingleRejectQuote,
  fetchApprovalQuote,
} from "../quotes/quotes.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { isDate } from "moment";

// MUI v6 DatePicker imports
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

type QuoteRejectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  quoteId: number;
};

type UpdatedValue = string | Date;

const QuoteRejectModal: FC<QuoteRejectModalProps> = (props) => {
  const { quoteId, isOpen, onClose } = props;

  const [dateRejected, setDateRejected] = useState<Date>(new Date());
  const [rejectionReason, setRejectionReason] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  const {
    isFetching,
    data: dataBenchmarkSingleApprovalQuote,
    refetch,
  } = useQuery({
    queryKey: [BENCHMARK_SINGLEAPPROVALQUOTE, quoteId],
    queryFn: () => fetchApprovalQuote(quoteId),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id === "dateRejected") {
        setDateRejected(updatedValue as Date);
      } else if (id === "rejectionReason") {
        setRejectionReason(updatedValue as string);
      }
    },
    []
  );

  useEffect(() => {
    if (isOpen && quoteId) {
      setDateRejected(new Date());
      setRejectionReason("");
      setIsShowLoader(false);
      setValidationMsg("");
    }
  }, [isOpen, quoteId]);

  const handleSubmit = async () => {
    if (
      dateRejected === undefined ||
      dateRejected === null ||
      !isDate(dateRejected)
    ) {
      setValidationMsg("Please select Rejection date");
      return;
    }
    if (rejectionReason.trim() === "") {
      setValidationMsg("Please provide a reason for rejection");
      return;
    }

    setIsShowLoader(true);

    updateSingleRejectQuote(quoteId, rejectionReason, dateRejected).then(
      (updateResult) => {
        setIsShowLoader(false);

        if (updateResult === "Success") {
          Swal.fire({
            customClass: {
              popup: "sweetAlert",
            },
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
            title: "Quote Rejected Successfully!",
          });
          onClose();
        } else if (updateResult === "mailnotsent") {
          Swal.fire({
            customClass: ".swal2-confirm",
            icon: "warning",
            title:
              "Quote rejected but mail not sent to Benchmark, please contact notifications@benchmarkcos.com",
          });
        } else {
          Swal.fire({
            customClass: ".swal2-confirm",
            icon: "warning",
            title: "Failed to Reject Quote",
          });
        }
      }
    );
  };

  const handleCancel = () => {
    setDateRejected(new Date());
    setRejectionReason("");
    setIsShowLoader(false);
    setValidationMsg("");
    onClose();
  };

  return (
    <Modal show={isOpen} label="Reject Quote" onClose={handleCancel}>
      {validationMsg && (
        <div className="text-danger mb-2">{validationMsg}</div>
      )}
      <div className="quoteModalSection">
        <div className="modalForm">
          {(isShowLoader || isFetching) && <Loader />}
          <form>
            <div className="formControlGroup">
              <label className="formFieldLabel">
                Date Rejected<span className="text-danger">*</span>
              </label>
              <div className="formField">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Select date"
                    value={dayjs(dateRejected)}
                    onChange={(newVal: Dayjs | null) => {
                      if (newVal) {
                        setDateRejected(newVal.toDate());
                      }
                    }}
                    format={DATE_FORMAT_UPDATED}
                    disableFuture
                      slotProps={{
                      textField: {
                        size: "small",
                        error: false,
                        helperText: "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div className="formControlGroup">
              <label className="formFieldLabel">
                Rejection Reason<span className="text-danger">*</span>
              </label>
              <div className="formField">
                <textarea
                  id="rejectionReason"
                  className="form-control"
                  rows={4}
                  value={rejectionReason}
                  onChange={(e) =>
                    onChangeFieldValues("rejectionReason", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="formControlGroup">
              <label className="formFieldLabel"></label>
              <div className="formField actionButtons">
                <button
                  type="button"
                  className="btn cancelPwdBtn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn submitBtn"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteRejectModal;
